import { Phone, MessageCircle, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { SOCIAL } from "../data/siteData";
import { usePagePhone } from "../hooks/usePagePhone";
import { trackCTA } from "../lib/analytics";
import { makeoverWhatsAppUrl } from "../data/makeoverData";

/**
 * Fixed mobile sticky CTA bar — 3 equal buttons: Call · WhatsApp · Free Demo.
 * Height is fixed at 64px (+ device safe-area inset). The exact same height is
 * exposed via the CSS variable `--mobile-cta-h` on <body> so other floating
 * elements (WhatsApp FAB, spacer) can offset consistently.
 */
export default function MobileCTABar() {
    const { pathname } = useLocation();
    const { settings } = useSettings();
    const pagePhone = usePagePhone();

    // Hide on admin routes
    if (pathname.startsWith("/admin") || pathname === "/register") return null;

    const phone = pagePhone.tel;
    const wa = pagePhone.raw;
    // Online makeover pages only (offline Jabalpur makeover keeps the default flow).
    const isOnlineMakeover = pathname.startsWith("/online-makeover-classes");
    const waHrefFinal = isOnlineMakeover
        ? makeoverWhatsAppUrl(wa)
        : `https://wa.me/${wa}?text=${encodeURIComponent("Hi Anand Tutorials, I would like to know more about 1:1 tuition.")}`;

    // Every button uses the exact same structure so widths/heights match.
    const btnBase =
        "flex flex-col items-center justify-center gap-0.5 h-16 min-h-[64px] font-semibold transition-colors";

    return (
        <div
            className="xl:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-3 bg-white border-t border-slate-200 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)]"
            data-testid="mobile-cta-bar"
            style={{
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                paddingLeft: "env(safe-area-inset-left, 0px)",
                paddingRight: "env(safe-area-inset-right, 0px)",
            }}
        >
            <a
                href={`tel:${phone}`}
                onClick={() => trackCTA("call_now_clicked", { cta_location: "mobile-cta-bar" })}
                data-testid="mobile-cta-call"
                className={`${btnBase} text-[#1E3A8A] hover:bg-blue-50 active:bg-blue-100 border-r border-slate-100`}
            >
                <Phone className="w-5 h-5" aria-hidden="true" />
                <span className="text-[11px] leading-none">Call</span>
            </a>
            <a
                href={waHrefFinal}
                onClick={() => trackCTA("whatsapp_clicked", { cta_location: "mobile-cta-bar" })}
                target="_blank"
                rel="noreferrer"
                {...(isOnlineMakeover ? { "data-smart-contact-skip": "true" } : {})}
                data-testid="mobile-cta-whatsapp"
                className={`${btnBase} text-[#25D366] hover:bg-green-50 active:bg-green-100 border-r border-slate-100`}
            >
                <MessageCircle className="w-5 h-5" aria-hidden="true" />
                <span className="text-[11px] leading-none">WhatsApp</span>
            </a>
            <Link
                to="/register"
                onClick={() => trackCTA("book_demo_clicked", { cta_location: "mobile-cta-bar" })}
                data-testid="mobile-cta-demo"
                className={`${btnBase} bg-amber-500 text-[#0F172A] hover:bg-amber-400 active:bg-amber-600`}
            >
                <Sparkles className="w-5 h-5" aria-hidden="true" />
                <span className="text-[11px] leading-none">Free Demo</span>
            </Link>
        </div>
    );
}
